import { getCoreUtils, nz, serverLocale } from "./kCoreHandler";

/**
 * kTimeTextCore - compact human time texts for status lines: a duration ("40 s", "2 min 40 s",
 * "52 min", "2 h 10 min"), the time left until a unix-seconds instant, and a local "HH:mm" clock.
 * Pure, no browser APIs: safe in Node, Azure Functions and the browser.
 */
export class kTimeTextCore {
    /**
     * A compact duration from seconds. Default: whole seconds - "40 s", "2 min 40 s", "1 h", "1 h 5 min";
     * below FloorSeconds (default 5) "under 5 s". RoundUpToMinutes: whole minutes rounded UP and never
     * seconds - "52 min", "2 h", "2 h 10 min"; zero or less "under a minute".
     */
    public static DurationText = (Seconds: number, RoundUpToMinutes: boolean = false, FloorSeconds: number = 5): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (RoundUpToMinutes === true) {
                const Min: number = Math.ceil(Seconds / 60);
                if (Min <= 0) { return "under a minute"; }
                return kTimeTextCore.MinutesText(Min);
            }
            if (Seconds < FloorSeconds) { return "under " + String(FloorSeconds) + " s"; }
            const TotalSeconds: number = Math.round(Seconds);
            if (TotalSeconds < 60) { return String(TotalSeconds) + " s"; }
            const TotalMinutes: number = Math.floor(TotalSeconds / 60);
            const Rest: number = TotalSeconds - TotalMinutes * 60;
            if (TotalMinutes < 60 && Rest !== 0) { return String(TotalMinutes) + " min " + String(Rest) + " s"; }
            return kTimeTextCore.MinutesText(TotalMinutes);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kTimeTextCore.DurationText");
            return "";
        }
    };

    /** Time left until a unix-seconds instant, in whole minutes rounded up: "52 min", "2 h 10 min"; past or now "under a minute". */
    public static UntilText = (UnixSec: number, NowMs: number = Date.now()): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            const Min: number = Math.ceil((UnixSec * 1000 - NowMs) / 60000);
            if (Min <= 0) { return "under a minute"; }
            return kTimeTextCore.MinutesText(Min);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kTimeTextCore.UntilText");
            return "";
        }
    };

    /** Like UntilText, but from a day on in days and hours (whole hours rounded up): "2 d 13 h", "1 d", "3 d 1 h";
     *  under a day exactly as UntilText ("52 min", "2 h 10 min"). For reset times days away (a weekly quota). */
    public static UntilDaysText = (UnixSec: number, NowMs: number = Date.now()): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            const Min: number = Math.ceil((UnixSec * 1000 - NowMs) / 60000);
            if (Min < 24 * 60) { return kTimeTextCore.UntilText(UnixSec, NowMs); }
            const Hours: number = Math.ceil(Min / 60);
            const Days: number = Math.floor(Hours / 24);
            const Rest: number = Hours - Days * 24;
            if (Rest === 0) { return String(Days) + " d"; }
            return String(Days) + " d " + String(Rest) + " h";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kTimeTextCore.UntilDaysText");
            return "";
        }
    };

    /** When a unix-seconds instant falls, in the reader's local time: "14:05" today, "tomorrow 14:05", else the short
     *  weekday within a week ("Sat 14:05") and the short date beyond ("8 Nov 14:05"); "" when it is not a number. The
     *  names come from the locale (CLDR, no hand-rolled tables): Locale = a BCP-47 tag, "" = the host locale. */
    public static WhenText = (UnixSec: number, NowMs: number = Date.now(), Locale: string = ""): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            const At: Date = new Date(UnixSec * 1000);
            const Clock: string = kTimeTextCore.ClockOfDate(At);
            if (Clock === "") { return ""; }
            const Today: Date = new Date(NowMs);
            const DayMs: number = 86400000;
            const Days: number = Math.round((new Date(At.getFullYear(), At.getMonth(), At.getDate()).getTime() -
                new Date(Today.getFullYear(), Today.getMonth(), Today.getDate()).getTime()) / DayMs);
            if (Days === 0) { return Clock; }
            if (Days === 1) { return "tomorrow " + Clock; }
            const UseLocale: string = nz(Locale).length > 0 ? Locale : serverLocale();
            if (Days > 1 && Days < 7) { return At.toLocaleDateString(UseLocale, { weekday: "short" }) + " " + Clock; }
            return At.toLocaleDateString(UseLocale, { day: "numeric", month: "short" }) + " " + Clock;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kTimeTextCore.WhenText");
            return "";
        }
    };

    /** Local "HH:mm" (the reader's time zone) of a unix-seconds instant; "" when it is not a number. */
    public static ClockText = (UnixSec: number): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            return kTimeTextCore.ClockOfDate(new Date(UnixSec * 1000));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kTimeTextCore.ClockText");
            return "";
        }
    };

    /** Local "HH:mm" of an ISO timestamp; "" when it does not parse. */
    public static ClockOfIso = (Iso: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            const At: number = Date.parse(nz(Iso));
            if (isNaN(At) === true) { return ""; }
            return kTimeTextCore.ClockOfDate(new Date(At));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kTimeTextCore.ClockOfIso");
            return "";
        }
    };

    /** Local "HH:mm" of a Date; "" for an invalid Date. */
    public static ClockOfDate = (When: Date): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (isNaN(When.getTime()) === true) { return ""; }
            return String(When.getHours()).padStart(2, "0") + ":" + String(When.getMinutes()).padStart(2, "0");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kTimeTextCore.ClockOfDate");
            return "";
        }
    };

    // Whole minutes (> 0) -> "52 min" / "2 h" / "2 h 10 min".
    private static MinutesText = (Min: number): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            const H: number = Math.floor(Min / 60);
            if (H === 0) { return String(Min) + " min"; }
            if (Min % 60 === 0) { return String(H) + " h"; }
            return String(H) + " h " + String(Min % 60) + " min";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kTimeTextCore.MinutesText");
            return "";
        }
    };
    /** "YYYY-MM-DD HH:mm" by PURE TEXT SLICE of a stored ISO timestamp - characters 0-10 and 11-16,
     *  i.e. the STORED values: no Date.parse, no local-timezone conversion. Deliberately different
     *  from ClockOfIso/ClockOfDate, which parse and convert to local time. The caller owns EVERY
     *  guard: KErrorMode, empty/short-input fallback and the catch stay at the call sites, because
     *  the two lifted copies disagree observably on all three (CTS _Fallback answers with the
     *  original string, MOSE FormatIso answers "").
     *  DOCUMENTED EXCEPTION: pure computation on an already-length-checked string; no throw path,
     *  no KErrorMode gate, no fallback - those live at the call sites. */
    public static MinuteStampOfIso = (Iso: string): string => {
        return Iso.substring(0, 10) + " " + Iso.substring(11, 16);
    };
}
